/**
 * Organization Service
 *
 * API calls for organizations, sites, hierarchy, roles, and talk groups
 * Hierarchy: Organization → Site
 */
import apiClient from './apiClient';
import { Country, State } from 'country-state-city';

// Placeholder org ID - will come from auth context after login
// This should be replaced with actual org context in production
const DEFAULT_ORG_ID = 1;

const organizationService = {
  /**
   * List the timezone reference data used by the site settings selector.
   * @returns {Promise<Array<{id: number, timezoneIana: string, portalLabel: string}>>}
   */
  listTimezones: async () => {
    const response = await apiClient.get('/api/timezones');
    return response.data;
  },

  // =============================================================================
  // ORGANIZATIONS (Top-level)
  // =============================================================================

  /**
   * List all organizations with offset-based pagination
   * @param {number} offset - Offset for pagination (default 0)
   * @param {number} limit - Page size (default 50, max 200)
   * @param {AbortSignal} signal - Optional AbortSignal for cancellation
   * @returns {Promise<Object>} Paged response with organizations
   */
  listOrganizations: async (offset = 0, limit = 50, signal = null) => {
    const response = await apiClient.get('/api/organizations', {
      params: { offset, limit },
      signal,
    });
    return response.data;
  },

  /**
   * Get organization details by ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Organization details
   */
  getOrganization: async (orgId) => {
    const response = await apiClient.get(`/api/organizations/${orgId}`);
    return response.data;
  },

  /**
   * Create a new organization
   * @param {Object} orgData - Organization creation data
   * @returns {Promise<Object>} Created organization with ID
   */
  createOrganization: async (orgData) => {
    const response = await apiClient.post('/api/organizations', orgData);
    return response.data;
  },

  /**
   * Update organization details
   * @param {number} orgId - Organization ID
   * @param {Object} orgData - Organization update data
   * @returns {Promise<Object>} Updated organization
   */
  updateOrganization: async (orgId, orgData) => {
    const response = await apiClient.put(`/api/organizations/${orgId}`, orgData);
    return response.data;
  },

  /**
   * Accept the Terms of Service for an organization. Idempotent from the
   * caller's perspective: a second call returns HTTP 409 which the UI
   * translates into "already accepted" state (button stays disabled).
   *
   * @param {number} orgId - Organization ID
   * @param {string} [acceptedBy] - Optional display name of the acceptor;
   *   when omitted, the backend uses the currently authenticated user.
   * @returns {Promise<Object>} Updated organization (includes tosAccepted,
   *   tosAcceptedBy, tosAcceptedAt)
   */
  acceptTermsOfService: async (orgId, acceptedBy) => {
    const body = acceptedBy ? { acceptedBy } : {};
    const response = await apiClient.post(
      `/api/organizations/${orgId}/tos/accept`,
      body
    );
    return response.data;
  },

  /**
   * Delete an organization
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteOrganization: async (orgId) => {
    await apiClient.delete(`/api/organizations/${orgId}`);
  },

  /**
   * Create organization from Workflow form data.
   * Maps WorkflowPage form fields → OrganizationCreateRequest.
   * @param {Object} form - WorkflowPage form state (step 0 fields)
   * @returns {Promise<Object>} { orgId }
   */
  createOrganizationFromWorkflow: async (form) => {

    const adminFullName = [form.adminFirstName, form.adminLastName].filter(Boolean).join(' ');
    const adminPhone = form.adminPhone
      ? `${form.adminPhoneCode || ''}${form.adminPhone}`.trim()
      : null;

    const payload = {
      name: form.orgName,
      displayName: form.orgName,
      language: form.orgLanguage || null,
      description: form.orgDescription || null,
      addressLine1: form.orgAddress1 || null,
      addressLine2: form.orgAddress2 || null,
      city: form.orgCity || null,
      state: form.orgState || null,
      zipCode: form.orgPostalCode || null,
      country: form.orgCountry || null,
      replyToEmail: form.orgEmail || null,
      // primaryContactName/Email/Phone mirrors super admin for now
      primaryContactName: adminFullName || null,
      primaryContactEmail: form.adminEmail || null,
      primaryContactPhone: adminPhone || null,
      superAdminFirstName: form.adminFirstName || null,
      superAdminLastName: form.adminLastName || null,
      superAdminJobTitle: form.adminJobTitle || null,
      superAdminEmail: form.adminEmail || null,
      superAdminPhone: adminPhone || null,
    };

    const response = await apiClient.post('/api/organizations', payload);
    return response.data; // { orgId }
  },

  /**
   * Update an existing organization from Workflow form data.
   * Maps WorkflowPage form fields → OrganizationUpdateRequest.
   * @param {number} orgId - Organization ID
   * @param {Object} form - WorkflowPage form state (step 0 fields)
   * @returns {Promise<Object>} Updated organization
   */
  updateOrganizationFromWorkflow: async (orgId, form) => {

    const adminFullName = [form.adminFirstName, form.adminLastName].filter(Boolean).join(' ');
    const adminPhone = form.adminPhone
      ? `${form.adminPhoneCode || ''}${form.adminPhone}`.trim()
      : null;

    const payload = {
      name: form.orgName,
      displayName: form.orgName,
      language: form.orgLanguage || null,
      description: form.orgDescription || null,
      addressLine1: form.orgAddress1 || null,
      addressLine2: form.orgAddress2 || null,
      city: form.orgCity || null,
      state: form.orgState || null,
      zipCode: form.orgPostalCode || null,
      country: form.orgCountry || null,
      replyToEmail: form.orgEmail || null,
      primaryContactName: adminFullName || null,
      primaryContactEmail: form.adminEmail || null,
      primaryContactPhone: adminPhone || null,
      superAdminFirstName: form.adminFirstName || null,
      superAdminLastName: form.adminLastName || null,
      superAdminJobTitle: form.adminJobTitle || null,
      superAdminEmail: form.adminEmail || null,
      superAdminPhone: adminPhone || null,
    };

    const response = await apiClient.put(`/api/organizations/${orgId}`, payload);
    return response.data;
  },

  // =============================================================================
  // ORGS — Aliases for backward compatibility
  // In the old hierarchy, "orgs" were chains under organizations.
  // Now that chains are removed (Org → Site), these delegate to organization methods.
  // =============================================================================

  listOrgs: async (page = 0, size = 50) => {
    return organizationService.listOrganizations(page, size);
  },

  listOrgsByOrg: async (orgId, page = 0, size = 50) => {
    // Chains under org no longer exist; return the org itself
    return organizationService.listOrganizations(page, size);
  },

  createOrg: async (orgData) => {
    return organizationService.createOrganization(orgData);
  },

  getOrg: async (orgId) => {
    return organizationService.getOrganization(orgId);
  },

  updateOrg: async (orgId, orgData) => {
    return organizationService.updateOrganization(orgId, orgData);
  },

  updateOrgStatus: async (orgId, status) => {
    return organizationService.updateOrganization(orgId, { status });
  },

  deleteOrg: async (orgId) => {
    return organizationService.deleteOrganization(orgId);
  },

  // Legacy aliases (deprecated — use organization or org methods above)
  listCompanies: async (page = 0, size = 50) => {
    return organizationService.listOrganizations(page, size);
  },
  getCompany: async (orgId) => {
    return organizationService.getOrganization(orgId);
  },
  createCompany: async (companyData) => {
    return organizationService.createOrganization(companyData);
  },
  updateCompany: async (orgId, companyData) => {
    return organizationService.updateOrganization(orgId, companyData);
  },
  updateCompanyStatus: async (orgId, status) => {
    return organizationService.updateOrganization(orgId, { status });
  },
  deleteCompany: async (orgId) => {
    return organizationService.deleteOrganization(orgId);
  },

  // =============================================================================
  // SITES (Physical locations under orgs)
  // =============================================================================

  /**
   * List sites for an organization
   * @param {number} orgId - Organization ID
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @param {AbortSignal} signal - Optional AbortSignal for cancellation
   * @returns {Promise<Object>} Paged response with sites
   */
  listSites: async (orgId = DEFAULT_ORG_ID, page = 0, size = 50, signal = null) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/sites`, {
      params: { page, size },
      signal,
    });
    return response.data;
  },

  /**
   * List all sites under an organization
   * @param {number} orgId - Organization ID
   * @param {number} page - Page number (0-indexed)
   * @param {number} size - Page size
   * @returns {Promise<Object>} Paged response with sites
   */
  listSitesByOrg: async (orgId, page = 0, size = 200) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/sites`, {
      params: { page, size }
    });
    return response.data;
  },

  /**
   * List sites with hierarchy assignments for an organization
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} Sites with hierarchy node info
   */
  listSitesWithHierarchy: async (orgId) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/sites-with-hierarchy`);
    return response.data;
  },

  /**
   * Get site details by ID
   * @param {number} siteId - Site ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Site details
   */
  getSite: async (siteId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/sites/${siteId}`);
    return response.data;
  },

  /**
   * Create a new site
   * @param {Object} siteData - Site creation data (must include orgId)
   * @returns {Promise<Object>} Created site with ID
   */
  createSite: async (siteData) => {
    const orgId = siteData.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(`/api/orgs/${orgId}/sites`, siteData);
    return response.data;
  },

  /**
   * Create site from Workflow form data.
   * Maps WorkflowPage form fields → SiteCreateRequest.
   * @param {Object} form - WorkflowPage form state (step 1 fields + orgId from step 0)
   * @returns {Promise<Object>} { siteId }
   */
  createSiteFromWorkflow: async (form) => {

    const phone = form.phoneNumber
      ? `${form.phoneCode || ''}${form.phoneNumber}`.trim()
      : null;

    const payload = {
      orgId: form.orgId || form.selectedOrgId,
      siteCode: null, // auto-generate
      siteName: form.siteName,
      regionName: form.regionName || null,
      timezone: form.timeZone || null,
      language: form.language || null,
      labels: form.labels || null,
      description: form.description || null,
      addressLine1: form.address || null,
      addressLine2: null,
      city: form.city || null,
      state: form.state || null,
      zipCode: form.postalCode || null,
      country: form.country || null,
      primaryContactName: null,
      primaryContactEmail: null,
      primaryContactPhone: phone || null,
      replyToEmail: form.replyToEmail || null,
    };

    console.debug('[organizationService] createSiteFromWorkflow payload:', payload);
    const orgId = payload.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(`/api/orgs/${orgId}/sites`, payload);
    return response.data; // { siteId }
  },

  /**
   * Update site details
   * @param {number} siteId - Site ID
   * @param {Object} siteData - Site update data
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated site
   */
  updateSite: async (siteId, siteData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/sites/${siteId}`, siteData);
    return response.data;
  },

  /**
   * Update an existing site from Workflow form data.
   * Maps WorkflowPage form fields → SiteUpdateRequest.
   * @param {number} siteId - Site ID
   * @param {Object} form - WorkflowPage form state (step 1 fields)
   * @returns {Promise<Object>} Updated site
   */
  updateSiteFromWorkflow: async (siteId, form) => {

    const phone = form.phoneNumber
      ? `${form.phoneCode || ''}${form.phoneNumber}`.trim()
      : null;

    const payload = {
      siteName: form.siteName,
      regionName: form.regionName || null,
      timezone: form.timeZone || null,
      language: form.language || null,
      labels: form.labels || null,
      description: form.description || null,
      addressLine1: form.address || null,
      addressLine2: null,
      city: form.city || null,
      state: form.state || null,
      zipCode: form.postalCode || null,
      country: form.country || null,
      primaryContactPhone: phone || null,
      replyToEmail: form.replyToEmail || null,
    };
    console.log(payload);

    console.debug('[organizationService] updateSiteFromWorkflow payload:', payload);
    const orgId = form.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.put(`/api/orgs/${orgId}/sites/${siteId}`, payload);
    return response.data;
  },

  /**
   * Delete a site
   * @param {number} siteId - Site ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteSite: async (siteId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/sites/${siteId}`);
  },

  // Legacy aliases for backward compatibility (deprecated - use site methods)
  listStores: async (orgId = DEFAULT_ORG_ID, page = 0, size = 50) => {
    return organizationService.listSites(orgId, page, size);
  },
  getStore: async (siteId) => {
    return organizationService.getSite(siteId);
  },
  createStore: async (storeData) => {
    return organizationService.createSite(storeData);
  },
  updateStore: async (siteId, storeData) => {
    return organizationService.updateSite(siteId, storeData);
  },
  deleteStore: async (siteId) => {
    return organizationService.deleteSite(siteId);
  },

  // =============================================================================
  // HIERARCHY LEVELS
  // =============================================================================

  /**
   * Get hierarchy levels for an organization
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of hierarchy levels
   */
  getHierarchyLevels: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/hierarchy-levels`);
    return response.data;
  },

  /**
   * Get hierarchy levels for an organization (alias)
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of hierarchy levels
   */
  getHierarchyLevelsByOrg: async (orgId) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/hierarchy-levels`);
    return response.data;
  },

  /**
   * Create a new hierarchy level
   * @param {Object} levelData - Hierarchy level data (must include orgId)
   * @returns {Promise<Object>} Created level with ID
   */
  createHierarchyLevel: async (levelData) => {
    const orgId = levelData.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(`/api/orgs/${orgId}/hierarchy-levels`, levelData);
    return response.data;
  },

  /**
   * Update a hierarchy level
   * @param {number} hierarchyLevelId - Hierarchy level ID
   * @param {Object} levelData - Hierarchy level update data
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated level
   */
  updateHierarchyLevel: async (hierarchyLevelId, levelData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/hierarchy-levels/${hierarchyLevelId}`, levelData);
    return response.data;
  },

  /**
   * Delete a hierarchy level
   * @param {number} hierarchyLevelId - Hierarchy level ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteHierarchyLevel: async (hierarchyLevelId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/hierarchy-levels/${hierarchyLevelId}`);
  },

  // =============================================================================
  // HIERARCHY NODES
  // =============================================================================

  /**
   * Get hierarchy nodes for an organization (flat list)
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of hierarchy nodes
   */
  getHierarchyNodes: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/hierarchy-nodes`);
    return response.data;
  },

  /**
   * Get hierarchy nodes for an organization (flat list) — alias
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of hierarchy nodes
   */
  getHierarchyNodesByOrg: async (orgId) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/hierarchy-nodes`);
    return response.data;
  },

  /**
   * Get hierarchy tree for an organization
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} Hierarchy tree structure
   */
  getHierarchyTree: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/hierarchy-tree`);
    return response.data;
  },

  /**
   * Get a single hierarchy node
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Hierarchy node details
   */
  getHierarchyNode: async (hierarchyNodeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/hierarchy-nodes/${hierarchyNodeId}`);
    return response.data;
  },

  /**
   * Create a new hierarchy node
   * @param {Object} nodeData - Hierarchy node data (must include orgId)
   * @returns {Promise<Object>} Created node with ID
   */
  createHierarchyNode: async (nodeData) => {
    const orgId = nodeData.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(`/api/orgs/${orgId}/hierarchy-nodes`, nodeData);
    return response.data;
  },

  /**
   * Update hierarchy node details
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {Object} nodeData - Hierarchy node update data
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated node
   */
  updateHierarchyNode: async (hierarchyNodeId, nodeData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/hierarchy-nodes/${hierarchyNodeId}`, nodeData);
    return response.data;
  },

  /**
   * Delete a hierarchy node
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteHierarchyNode: async (hierarchyNodeId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/hierarchy-nodes/${hierarchyNodeId}`);
  },

  /**
   * Assign a site to a hierarchy node
   * @param {number} siteId - Site ID
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Assignment result
   */
  assignSiteToHierarchy: async (siteId, hierarchyNodeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/site-hierarchy-map`, {
      siteId,
      hierarchyNodeId
    });
    return response.data;
  },

  /**
   * Remove site from hierarchy node
   * @param {number} siteId - Site ID
   * @param {number} hierarchyNodeId - Hierarchy node ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  removeSiteFromHierarchy: async (siteId, hierarchyNodeId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/site-hierarchy-map/${siteId}/${hierarchyNodeId}`);
  },

  // =============================================================================
  // PORTAL ROLES
  // =============================================================================

  /**
   * Get portal roles for an organization
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of portal roles
   */
  getPortalRoles: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/portal-roles`);
    return response.data;
  },

  /**
   * Get a single portal role
   * @param {number} roleId - Role ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Portal role details
   */
  getPortalRole: async (roleId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/portal-roles/${roleId}`);
    return response.data;
  },

  /**
   * Create a new portal role
   * @param {Object} roleData - Portal role data (must include orgId)
   * @returns {Promise<Object>} Created role with ID
   */
  createPortalRole: async (roleData) => {
    const orgId = roleData.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(`/api/orgs/${orgId}/portal-roles`, roleData);
    return response.data;
  },

  /**
   * Delete a portal role
   * @param {number} roleId - Role ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deletePortalRole: async (roleId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/portal-roles/${roleId}`);
  },

  // =============================================================================
  // TALK GROUPS
  // =============================================================================

  /**
   * Get talk groups for an organization
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of talk groups
   */
  getTalkGroups: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/talk-groups`);
    return response.data;
  },

  /**
   * Get a single talk group
   * @param {number} talkGroupId - Talk group ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Talk group details
   */
  getTalkGroup: async (talkGroupId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/talk-groups/${talkGroupId}`);
    return response.data;
  },

  /**
   * Create a new talk group
   * @param {Object} groupData - Talk group data (must include orgId)
   * @returns {Promise<Object>} Created group with ID
   */
  createTalkGroup: async (groupData) => {
    const orgId = groupData.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(`/api/orgs/${orgId}/talk-groups`, groupData);
    return response.data;
  },

  /**
   * Update a talk group
   * @param {number} talkGroupId - Talk group ID
   * @param {Object} groupData - Fields to update
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated talk group
   */
  updateTalkGroup: async (talkGroupId, groupData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/talk-groups/${talkGroupId}`, groupData);
    return response.data;
  },

  /**
   * Delete a talk group
   * @param {number} talkGroupId - Talk group ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteTalkGroup: async (talkGroupId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/talk-groups/${talkGroupId}`);
  },
};

export default organizationService;
